import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ShieldCheck, Sparkle, Files, ReceiptX, Plus, Trash, PencilSimple,
  FloppyDisk, ArrowSquareOut, CheckCircle, Warning, Info, Plug, PaperPlaneTilt, MapPin,
  Image as ImageIcon, FilePdf, Paperclip, ChatCircleDots, DeviceMobile, Broom, Palette, PlugsConnected as Webhook,
  Truck, ArrowsClockwise, Warehouse, ClockCounterClockwise, Package, Star, Robot, WhatsappLogo, Stethoscope,
  FileText, GitMerge, ChartLineUp, Bell,
} from "@phosphor-icons/react";
import SiriShortcutsPanel from "@/components/SiriShortcutsPanel";
import ReportTemplatesPanel from "@/components/ReportTemplatesPanel";
import WebhooksPanel from "@/components/OutboundWebhooksPanel";
import EquipmentPanel from "@/components/EquipmentPanel";
import BkpayBridgePanel from "@/components/BkpayBridgePanel";
import QuoteAcceptedDialog from "@/components/QuoteAcceptedDialog";
import WorkshopPanel from "@/components/WorkshopPanel";
import AuditLogPanel from "@/components/AuditLogPanel";
import MaterialCatalogPanel from "@/components/MaterialCatalogPanel";
import ReportRecipientsPanel from "@/components/ReportRecipientsPanel";
import GoogleReviewPanel from "@/components/GoogleReviewPanel";
import MCPPanel from "@/components/MCPPanel";
import MCPAuditPanel from "@/components/MCPAuditPanel";
import DepotSettingsPanel from "@/components/DepotSettingsPanel";
import BrevoPanel from "@/components/BrevoPanel";
import DbHealthPanel from "@/components/DbHealthPanel";
import AxonautImportPanel from "@/components/AxonautImportPanel";
import ClientDuplicatesPanel from "@/components/ClientDuplicatesPanel";
import UserMergePanel from "@/components/UserMergePanel";
import ClientReviewsPanel from "@/components/ClientReviewsPanel";
import ProfitabilityPanel from "@/components/ProfitabilityPanel";
import SavAstreintePanel from "@/components/SavAstreintePanel";

const MODELS = [
  { provider: "anthropic", model: "claude-sonnet-4-6", label: "Claude Sonnet 4.6 (recommandé)" },
  { provider: "anthropic", model: "claude-opus-4-7", label: "Claude Opus 4.7 (raisonnement max)" },
  { provider: "anthropic", model: "claude-haiku-4-5-20251001", label: "Claude Haiku 4.5 (rapide)" },
  { provider: "openai", model: "gpt-5.6-terra", label: "GPT 5.6 Terra" },
  { provider: "openai", model: "gpt-5.4", label: "GPT 5.4" },
  { provider: "gemini", model: "gemini-3.1-pro-preview", label: "Gemini 3.1 Pro" },
];

export default function AdminConsole() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isAdmin = user?.role === "admin" || user?.role === "compta";
  const isAssociate = user?.role === "associate";

  // Tabs the associate is allowed to see — operational panels that don't
  // expose API credentials, payment config, external secrets, or audit history.
  const ASSOCIATE_TABS = new Set(["templates", "reports", "equipment", "workshop", "catalog", "recipients", "rating", "siri"]);

  const [tab, setTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const requested = params.get("tab");
    if (requested && (isAdmin || (isAssociate && ASSOCIATE_TABS.has(requested)))) return requested;
    return isAssociate ? "templates" : "ai";
  });

  if (!isAdmin && !isAssociate) {
    return (
      <div className="p-6 space-y-3">
        <div className="rounded-2xl bg-red-500/10 border border-red-500/30 p-6 text-center">
          <ShieldCheck size={32} className="mx-auto text-red-400 mb-3" weight="duotone" />
          <div className="font-display font-bold text-lg">Accès réservé à l&apos;équipe de direction</div>
          <div className="text-sm text-white/60 mt-1">Cette console est uniquement accessible au gérant et à l&apos;associé.</div>
          <Button className="mt-4 bg-white/10 hover:bg-white/15" onClick={() => nav("/reglages")}>Retour</Button>
        </div>
      </div>
    );
  }

  const ALL_TABS = [
    { k: "ai", label: "Assistant IA", icon: Sparkle },
    { k: "astreinte", label: "Astreinte SAV", icon: Bell },
    { k: "templates", label: "Fiches types", icon: Files },
    { k: "reports", label: "Modèles rapport", icon: Palette },
    { k: "equipment", label: "Outillage", icon: Truck },
    { k: "workshop", label: "Atelier", icon: Warehouse },
    { k: "quotes", label: "À passer en devis", icon: ReceiptX },
    { k: "axonaut", label: "Axonaut", icon: Plug },
    { k: "axonaut-import", label: "Import Axonaut", icon: FileText },
    { k: "duplicates", label: "Doublons clients", icon: GitMerge },
    { k: "user-merge", label: "Fusion users", icon: GitMerge },
    { k: "brevo", label: "Brevo (SMS + WhatsApp)", icon: WhatsappLogo },
    { k: "siri", label: "Raccourcis Siri", icon: DeviceMobile },
    { k: "webhooks", label: "Webhooks", icon: Webhook },
    { k: "catalog", label: "Catalogue matériel", icon: Package },
    { k: "recipients", label: "Destinataires rapports", icon: PaperPlaneTilt },
    { k: "depot", label: "Dépôt payroll", icon: MapPin },
    { k: "rating", label: "Avis Google SAV", icon: Star },
    { k: "reviews", label: "Avis Clients", icon: Star },
    { k: "mcp", label: "MCP (Claude Desktop)", icon: Robot },
    { k: "audit", label: "Journal d'audit", icon: ClockCounterClockwise },
    { k: "dbhealth", label: "Santé de la base", icon: Stethoscope },
    { k: "prod", label: "Mise en prod", icon: Broom },
  ];
  const TABS = isAssociate ? ALL_TABS.filter((t) => ASSOCIATE_TABS.has(t.k)) : ALL_TABS;

  return (
    <div className="p-4 space-y-4">
      <div className="rounded-2xl bg-gradient-to-br from-[#005CFF]/20 to-[#005CFF]/5 border border-[#005CFF]/30 p-4 flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-[#005CFF] flex items-center justify-center shadow-[0_0_20px_rgba(0,92,255,0.4)]">
          <ShieldCheck size={22} weight="fill" color="white" />
        </div>
        <div className="flex-1">
          <div className="font-display font-semibold">{isAssociate ? "Console associé" : "Console gérant"}</div>
          <div className="text-xs text-white/60">
            {isAssociate
              ? "Fiches types, modèles de rapport, outillage, catalogue matériel. Les intégrations sensibles (Twilio, Axonaut, webhooks, audit) restent réservées au gérant."
              : "Réglages avancés — visible uniquement par vous"}
          </div>
        </div>
      </div>

      <div className="flex gap-1 bg-white/5 border border-white/10 rounded-full p-1 overflow-x-auto no-scrollbar">
        {TABS.map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`admin-tab-${t.k}`}
            className={`flex items-center gap-1.5 shrink-0 text-xs px-3 py-2 rounded-full ${tab === t.k ? "bg-[#005CFF] text-white" : "text-white/60"}`}
          >
            <t.icon size={14} weight="bold" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "ai" && <AISettingsPanel />}
      {tab === "astreinte" && <SavAstreintePanel />}
      {tab === "templates" && <TemplatesPanel />}
      {tab === "quotes" && <QuoteInboxPanel />}
      {tab === "axonaut" && <AxonautPanel />}
      {tab === "axonaut-import" && <AxonautImportPanel />}
      {tab === "duplicates" && <ClientDuplicatesPanel />}
      {tab === "user-merge" && <UserMergePanel />}
      {tab === "brevo" && <BrevoPanel />}
      {tab === "siri" && <SiriShortcutsPanel />}
      {tab === "reports" && <ReportTemplatesPanel />}
      {tab === "webhooks" && <WebhooksPanel />}
      {tab === "equipment" && <EquipmentPanel />}
      {tab === "workshop" && <WorkshopPanel />}
      {tab === "catalog" && <MaterialCatalogPanel />}
      {tab === "recipients" && <ReportRecipientsPanel />}
      {tab === "depot" && <DepotSettingsPanel />}
      {tab === "rating" && <GoogleReviewPanel />}
      {tab === "reviews" && <ClientReviewsPanel />}
      {tab === "mcp" && <MCPPanel />}
      {tab === "audit" && <AuditLogPanel />}
      {tab === "dbhealth" && <DbHealthPanel />}
      {tab === "prod" && <ProdCleanupPanel />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AI Settings
// ---------------------------------------------------------------------------
function AISettingsPanel() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/ai-settings").then((r) => setSettings(r.data));
  }, []);

  if (!settings) return <div className="h-40 rounded-2xl bg-white/5 animate-pulse" />;

  const modelValue = `${settings.provider}::${settings.model}`;
  const setModelValue = (v) => {
    const [provider, model] = v.split("::");
    setSettings((s) => ({ ...s, provider, model }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch("/admin/ai-settings", {
        system_prompt: settings.system_prompt,
        provider: settings.provider,
        model: settings.model,
        temperature: parseFloat(settings.temperature) || 0.3,
      });
      setSettings(r.data);
      toast.success("Réglages sauvegardés");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 space-y-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-white/50 mb-2">Modèle IA</div>
          <Select value={modelValue} onValueChange={setModelValue}>
            <SelectTrigger className="bg-white/5 border-white/10 h-12" data-testid="ai-model-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#111] border-white/10 text-white">
              {MODELS.map((m) => (
                <SelectItem key={`${m.provider}-${m.model}`} value={`${m.provider}::${m.model}`}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-widest text-white/50 mb-2">Température ({settings.temperature ?? 0.3})</div>
          <input
            type="range" min="0" max="1" step="0.1"
            value={settings.temperature ?? 0.3}
            onChange={(e) => setSettings((s) => ({ ...s, temperature: parseFloat(e.target.value) }))}
            className="w-full accent-[#005CFF]"
            data-testid="ai-temperature"
          />
          <div className="flex justify-between text-[10px] text-white/40 mt-1">
            <span>Précis</span><span>Créatif</span>
          </div>
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-widest text-white/50 mb-2">Consigne système (system prompt)</div>
          <Textarea
            value={settings.system_prompt || ""}
            onChange={(e) => setSettings((s) => ({ ...s, system_prompt: e.target.value }))}
            rows={10}
            className="bg-white/5 border-white/10 text-white font-mono text-xs leading-relaxed"
            data-testid="ai-system-prompt"
          />
          <div className="text-[10px] text-white/40 mt-1">Cette consigne définit le comportement de l&apos;assistant IA pour toute l&apos;équipe.</div>
        </div>

        <Button onClick={save} disabled={saving} className="w-full h-12 bg-[#005CFF] hover:bg-[#0047CC] rounded-2xl" data-testid="ai-save-btn">
          <FloppyDisk size={16} /> {saving ? "Enregistrement…" : "Enregistrer"}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------
function TemplatesPanel() {
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null); // template or "new"
  const load = async () => {
    const r = await api.get("/templates");
    setTemplates(r.data);
  };
  useEffect(() => { load(); }, []);

  const remove = async (t) => {
    if (!window.confirm(`Supprimer "${t.name}" ?`)) return;
    await api.delete(`/admin/templates/${t.id}`);
    toast.success("Supprimé");
    load();
  };

  return (
    <div className="space-y-3">
      <Button
        onClick={() => setEditing({ name: "", category: "chantier", description: "", items: [] })}
        className="w-full h-12 bg-[#005CFF] hover:bg-[#0047CC] rounded-2xl"
        data-testid="template-new-btn"
      >
        <Plus size={16} weight="bold" /> Nouvelle fiche type
      </Button>

      {templates.length === 0 ? (
        <div className="rounded-2xl bg-white/[0.03] border border-dashed border-white/10 p-8 text-center">
          <Files size={28} className="mx-auto text-white/40 mb-2" weight="duotone" />
          <div className="text-sm text-white/60">Aucune fiche type</div>
          <div className="text-xs text-white/40 mt-1">Créez des checklists réutilisables pour vos chantiers récurrents (tableau électrique, PAC, contrôle d&apos;accès…).</div>
        </div>
      ) : (
        <div className="space-y-2">
          {templates.map((t) => (
            <div key={t.id} className="rounded-2xl bg-white/[0.03] border border-white/10 p-3 flex items-center gap-3" data-testid={`template-row-${t.id}`}>
              <div className="w-10 h-10 rounded-xl bg-[#005CFF]/20 flex items-center justify-center">
                <Files size={18} weight="duotone" className="text-[#005CFF]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{t.name}</div>
                <div className="text-xs text-white/50">{t.items?.length || 0} tâches · {t.category === "chantier" ? "Chantier" : "Intervention"}</div>
              </div>
              <button onClick={() => setEditing(t)} className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center active:scale-95"><PencilSimple size={14} /></button>
              <button onClick={() => remove(t)} className="w-9 h-9 rounded-full bg-white/5 border border-white/10 hover:border-red-500/40 hover:text-red-400 flex items-center justify-center active:scale-95"><Trash size={14} /></button>
            </div>
          ))}
        </div>
      )}

      <TemplateEditor
        template={editing}
        open={!!editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    </div>
  );
}

function TemplateEditor({ template, open, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", category: "chantier", description: "", items: [] });
  const [newItem, setNewItem] = useState("");
  const [saving, setSaving] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (template) {
      setForm({
        name: template.name || "",
        category: template.category || "chantier",
        description: template.description || "",
        items: (template.items || []).map((i) => ({ ...i })),
      });
      if (template.id) {
        api.get(`/templates/${template.id}/attachments`).then((r) => setAttachments(r.data)).catch(() => setAttachments([]));
      } else {
        setAttachments([]);
      }
    }
  }, [template]);

  const uploadAttachment = async (file) => {
    if (!template?.id) return toast.error("Enregistrez d'abord la fiche type");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/admin/templates/${template.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAttachments((a) => [...a, r.data]);
      toast.success("Pièce jointe ajoutée");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const deleteAttachment = async (aid) => {
    await api.delete(`/admin/templates/${template.id}/attachments/${aid}`);
    setAttachments((a) => a.filter((x) => x.id !== aid));
  };

  const addItem = () => {
    if (!newItem.trim()) return;
    setForm((f) => ({ ...f, items: [...f.items, { label: newItem, required: false }] }));
    setNewItem("");
  };
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nom requis");
    setSaving(true);
    try {
      if (template?.id) {
        await api.patch(`/admin/templates/${template.id}`, form);
        toast.success("Fiche type mise à jour");
      } else {
        await api.post("/admin/templates", form);
        toast.success("Fiche type créée");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md bg-[#111] border-white/10 text-white rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="template-editor">
        <DialogHeader>
          <DialogTitle className="font-display">{template?.id ? "Modifier fiche type" : "Nouvelle fiche type"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-white/60 text-[11px] uppercase tracking-widest">Nom *</Label>
            <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="bg-white/5 border-white/10 mt-1" data-testid="tpl-name" />
          </div>
          <div>
            <Label className="text-white/60 text-[11px] uppercase tracking-widest">Catégorie</Label>
            <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
              <SelectTrigger className="bg-white/5 border-white/10 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#111] border-white/10 text-white">
                <SelectItem value="chantier">Chantier</SelectItem>
                <SelectItem value="intervention">Intervention</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-white/60 text-[11px] uppercase tracking-widest">Description</Label>
            <Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={2} className="bg-white/5 border-white/10 mt-1" />
          </div>
          <div>
            <Label className="text-white/60 text-[11px] uppercase tracking-widest">Tâches ({form.items.length})</Label>
            <div className="space-y-1 mt-2">
              {form.items.map((it, i) => (
                <div key={it.id || `item-${i}-${it.label?.slice(0, 8)}`} className="flex items-center gap-2 rounded-lg bg-white/5 border border-white/10 px-3 py-2" data-testid={`tpl-item-${i}`}>
                  <span className="text-white/40 text-xs">{i + 1}</span>
                  <div className="flex-1 text-sm">{it.label}</div>
                  <button onClick={() => removeItem(i)} className="text-white/40 hover:text-red-400"><Trash size={12} /></button>
                </div>
              ))}
              <div className="flex gap-2">
                <Input
                  value={newItem}
                  onChange={(e) => setNewItem(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addItem())}
                  placeholder="Ajouter une tâche…"
                  className="bg-white/5 border-white/10 h-10 text-sm"
                  data-testid="tpl-new-item"
                />
                <Button onClick={addItem} className="h-10 bg-[#005CFF] hover:bg-[#0047CC]" data-testid="tpl-add-item"><Plus size={14} weight="bold" /></Button>
              </div>
            </div>
          </div>
          <div>
            <Label className="text-white/60 text-[11px] uppercase tracking-widest">Pièces jointes ({attachments.length})</Label>
            {!template?.id && (
              <div className="text-[10px] text-white/40 mt-1">Enregistrez la fiche type d&apos;abord pour joindre des photos/PDF.</div>
            )}
            {template?.id && (
              <div className="space-y-1.5 mt-2">
                {attachments.map((a) => (
                  <a
                    key={a.id}
                    href={fileUrl(a.id)}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-2 rounded-lg bg-white/5 border border-white/10 px-3 py-2 hover:border-[#005CFF]/40"
                    data-testid={`tpl-att-${a.id}`}
                  >
                    {a.kind === "photo" ? <ImageIcon size={14} className="text-[#005CFF]" weight="duotone" /> : a.kind === "pdf" ? <FilePdf size={14} className="text-red-400" weight="duotone" /> : <Paperclip size={14} className="text-white/60" />}
                    <div className="flex-1 text-xs truncate">{a.filename}</div>
                    <button type="button" onClick={(e) => { e.preventDefault(); deleteAttachment(a.id); }} className="text-white/40 hover:text-red-400"><Trash size={12} /></button>
                  </a>
                ))}
                <input ref={fileInputRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => e.target.files?.[0] && uploadAttachment(e.target.files[0])} />
                <Button type="button" variant="ghost" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="w-full border border-dashed border-white/15 h-10 text-xs" data-testid="tpl-add-attachment">
                  {uploading ? "Upload…" : <><Paperclip size={14} /> Ajouter photo ou PDF</>}
                </Button>
              </div>
            )}
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="ghost" onClick={onClose} className="flex-1 border border-white/10">Annuler</Button>
            <Button onClick={save} disabled={saving} className="flex-1 bg-[#005CFF] hover:bg-[#0047CC]" data-testid="tpl-save">
              {saving ? "…" : "Enregistrer"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Quote inbox
// ---------------------------------------------------------------------------
function QuoteInboxPanel() {
  const [data, setData] = useState(null);
  const [axonaut, setAxonaut] = useState(null);
  const [acceptDialog, setAcceptDialog] = useState(null); // { kind, item }
  const nav = useNavigate();

  const load = async () => {
    const [r, ax] = await Promise.all([api.get("/admin/quote-inbox"), api.get("/admin/axonaut-settings")]);
    setData(r.data);
    setAxonaut(ax.data);
  };
  useEffect(() => { load(); }, []);

  const markSent = async (kind, id) => {
    const ref = window.prompt(
      "Ce bouton ne crée PAS le devis dans Axonaut — il note juste que tu l'as déjà fait toi-même.\n\nRéférence Axonaut (optionnelle) :",
      "",
    );
    if (ref === null) return; // user cancelled
    try {
      await api.post("/admin/quote-inbox/mark-sent", { kind, id, axonaut_ref: ref || null });
      toast.success("Marqué comme envoyé manuellement");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const markAccepted = async (kind, item) => {
    setAcceptDialog({ kind, item });
  };

  const pushAxonaut = async (kind, id) => {
    if (!window.confirm("Créer le devis dans Axonaut maintenant ?")) return;
    try {
      const r = await api.post("/admin/quote-inbox/push-axonaut", { kind, id });
      toast.success(`Devis Axonaut créé — ref ${r.data.axonaut_ref}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!data) return <div className="h-40 rounded-2xl bg-white/5 animate-pulse" />;

  const total = (data.chantiers?.length || 0) + (data.interventions?.length || 0);
  const axonautReady = !!axonaut?.api_key_preview;

  return (
    <div className="space-y-3">
      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-amber-500/20 flex items-center justify-center">
          <ReceiptX size={22} className="text-amber-400" weight="duotone" />
        </div>
        <div className="flex-1">
          <div className="font-display font-bold text-lg">{total} en attente</div>
          <div className="text-xs text-white/60">Éléments signalés par l&apos;équipe pour passage en devis Axonaut</div>
        </div>
      </div>

      {/* Explanatory strip — clarifies what each action button does */}
      <div className="rounded-xl bg-[#005CFF]/10 border border-[#005CFF]/30 p-3 text-[11px] text-white/80 space-y-1">
        <div className="font-semibold text-[#7CB2FF] uppercase tracking-widest text-[10px]">Comment envoyer un devis dans Axonaut ?</div>
        <div>
          <b className="text-[#7CB2FF]">1. « Créer devis Axonaut » </b>
          <span className="text-white/60">→ crée automatiquement le devis dans Axonaut via l&apos;API. C&apos;est le bouton bleu principal.</span>
        </div>
        <div>
          <b className="text-emerald-400">2. « Devis accepté »</b>
          <span className="text-white/60"> → clique quand le client a signé le devis dans Axonaut, pour clôturer côté BKflow.</span>
        </div>
        <div>
          <b className="text-white/50">3. « Marquer envoyé (manuel) »</b>
          <span className="text-white/60"> → si tu as déjà créé le devis toi-même dans Axonaut, sans passer par ce bouton bleu.</span>
        </div>
        {!axonautReady && (
          <div className="mt-2 pt-2 border-t border-white/10 text-amber-300">
            <Warning size={11} weight="fill" className="inline mr-1" />
            Clé API Axonaut non configurée → seul le mode manuel est disponible. Va dans l&apos;onglet <b>Axonaut</b> pour la renseigner.
          </div>
        )}
      </div>

      {total === 0 ? (
        <div className="rounded-2xl bg-white/[0.03] border border-dashed border-white/10 p-8 text-center">
          <CheckCircle size={28} className="mx-auto text-emerald-400 mb-2" weight="duotone" />
          <div className="text-sm text-white/60">Rien à traiter — bravo</div>
        </div>
      ) : (
        <div className="space-y-3">
          {data.chantiers?.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-widest text-white/50 mb-2">Chantiers ({data.chantiers.length})</div>
              <div className="space-y-2">
                {data.chantiers.map((c) => (
                  <QuoteCard
                    key={c.id}
                    item={c}
                    subtitle={c.client?.name || "—"}
                    onOpen={() => nav(`/chantiers/${c.id}`)}
                    onSent={() => markSent("chantier", c.id)}
                    onAccepted={() => markAccepted("chantier", c)}
                    onAxonaut={axonautReady ? () => pushAxonaut("chantier", c.id) : null}
                  />
                ))}
              </div>
            </div>
          )}
          {data.interventions?.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-widest text-white/50 mb-2">Interventions ({data.interventions.length})</div>
              <div className="space-y-2">
                {data.interventions.map((iv) => (
                  <QuoteCard
                    key={iv.id}
                    item={iv}
                    subtitle={new Date(iv.scheduled_date).toLocaleString("fr-FR")}
                    onOpen={() => nav(`/chantiers/${iv.chantier_id}`)}
                    onSent={() => markSent("intervention", iv.id)}
                    onAccepted={() => markAccepted("intervention", iv)}
                    onAxonaut={axonautReady ? () => pushAxonaut("intervention", iv.id) : null}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {acceptDialog && (
        <QuoteAcceptedDialog
          open
          onClose={() => setAcceptDialog(null)}
          kind={acceptDialog.kind}
          entity={acceptDialog.item}
          estimatedAmount={acceptDialog.item?.estimated_amount}
          onSaved={() => { setAcceptDialog(null); load(); }}
        />
      )}
    </div>
  );
}

function QuoteCard({ item, subtitle, onOpen, onSent, onAccepted, onAxonaut }) {
  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-3" data-testid={`quote-${item.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-1 h-14 rounded-full bg-amber-400 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold truncate">{item.name || item.title}</div>
          <div className="text-xs text-white/50 truncate">{subtitle}</div>
          {item.quote_note && (
            <div className="text-xs text-white/70 mt-2 rounded-lg bg-amber-400/5 border border-amber-400/20 p-2">
              <span className="text-amber-400 text-[10px] uppercase tracking-widest">Note</span>
              <div className="mt-0.5">{item.quote_note}</div>
            </div>
          )}
          <div className="flex items-center gap-3 mt-2 text-[10px] text-white/40">
            <span>Signalé par {item.quote_flagged_by_name || "—"}</span>
            {item.estimated_amount != null && (
              <span className="font-mono text-white/70">≈ {Number(item.estimated_amount).toFixed(0)} €</span>
            )}
          </div>
        </div>
      </div>
      <div className="flex gap-2 mt-3 flex-wrap">
        <Button variant="ghost" onClick={onOpen} className="border border-white/10 h-9 text-xs px-3" data-testid={`quote-open-${item.id}`}>
          <ArrowSquareOut size={14} /> Ouvrir
        </Button>
        {onAxonaut ? (
          <Button
            onClick={onAxonaut}
            className="flex-1 bg-[#005CFF] hover:bg-[#0047CC] h-9 text-xs font-semibold shadow-lg shadow-[#005CFF]/30"
            data-testid={`quote-axonaut-${item.id}`}
            title="Crée automatiquement le devis dans Axonaut via l'API"
          >
            <PaperPlaneTilt size={14} weight="bold" /> Créer devis Axonaut
          </Button>
        ) : (
          <Button
            disabled
            className="flex-1 bg-white/5 h-9 text-xs opacity-60 cursor-not-allowed"
            title="Configure ta clé API Axonaut dans l'onglet Axonaut pour activer"
          >
            <PaperPlaneTilt size={14} /> API non configurée
          </Button>
        )}
        <Button onClick={onAccepted} className="flex-1 bg-emerald-600 hover:bg-emerald-700 h-9 text-xs" data-testid={`quote-accepted-${item.id}`}>
          <CheckCircle size={14} weight="bold" /> Devis accepté
        </Button>
        <Button
          variant="ghost"
          onClick={onSent}
          className="border border-white/10 h-9 text-[10.5px] px-2 text-white/60 hover:text-white"
          data-testid={`quote-sent-${item.id}`}
          title="Uniquement si tu as déjà créé le devis manuellement dans Axonaut"
        >
          <CheckCircle size={12} /> Marquer envoyé (manuel)
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Axonaut settings panel
// ---------------------------------------------------------------------------
function AxonautPanel() {
  const [settings, setSettings] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [autoPush, setAutoPush] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/axonaut-settings").then((r) => {
      setSettings(r.data);
      setAutoPush(!!r.data.auto_push);
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { auto_push: autoPush };
      if (apiKey.trim()) payload.api_key = apiKey.trim();
      const r = await api.patch("/admin/axonaut-settings", payload);
      setSettings(r.data);
      setApiKey("");
      toast.success("Réglages Axonaut sauvegardés");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <div className="h-40 rounded-2xl bg-white/5 animate-pulse" />;

  const configured = !!settings.api_key_preview;

  return (
    <div className="space-y-4">
      <div className={`rounded-2xl border p-4 flex items-center gap-3 ${configured ? "bg-emerald-500/10 border-emerald-500/30" : "bg-amber-500/10 border-amber-500/30"}`}>
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${configured ? "bg-emerald-500/20" : "bg-amber-500/20"}`}>
          <Plug size={20} className={configured ? "text-emerald-400" : "text-amber-400"} weight="duotone" />
        </div>
        <div className="flex-1">
          <div className="font-display font-semibold">Axonaut {configured ? "connecté" : "non configuré"}</div>
          <div className="text-xs text-white/60">
            {configured ? `Clé: ${settings.api_key_preview}` : "Ajoutez votre clé API pour créer les devis en 1 tap"}
          </div>
        </div>
      </div>

      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 space-y-3">
        <div>
          <Label className="text-white/60 text-[11px] uppercase tracking-widest">Clé API Axonaut</Label>
          <Input
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={configured ? "Laisser vide pour conserver" : "Coller votre clé Axonaut ici"}
            className="bg-white/5 border-white/10 mt-1 font-mono text-sm"
            data-testid="axonaut-key-input"
          />
          <div className="text-[10px] text-white/40 mt-1">
            Trouvable dans Axonaut → Paramètres → API. La clé est stockée chiffrée côté serveur.
          </div>
        </div>

        <div className="flex items-center justify-between rounded-xl bg-white/5 border border-white/10 p-3">
          <div>
            <div className="text-sm font-medium">Push automatique</div>
            <div className="text-xs text-white/50">Créer le devis dès qu&apos;un tech signale (sinon manuel)</div>
          </div>
          <Switch checked={autoPush} onCheckedChange={setAutoPush} className="data-[state=checked]:bg-[#005CFF]" data-testid="axonaut-auto-push" />
        </div>

        <Button onClick={save} disabled={saving} className="w-full h-12 bg-[#005CFF] hover:bg-[#0047CC] rounded-2xl" data-testid="axonaut-save">
          <FloppyDisk size={16} /> {saving ? "…" : "Enregistrer"}
        </Button>
      </div>

      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 text-sm text-white/60">
        <div className="font-display font-semibold text-white mb-2">Fonctionnement</div>
        <ol className="space-y-1 list-decimal list-inside text-xs">
          <li>Un tech signale un chantier « à passer en devis » avec note et montant estimé.</li>
          <li>Vous recevez une notification et voyez l&apos;élément dans l&apos;onglet « À passer en devis ».</li>
          <li>Bouton <b className="text-[#005CFF]">Envoyer Axonaut</b> crée un devis avec titre, client, montant, note publique et note interne.</li>
          <li>La référence Axonaut est enregistrée sur le chantier pour retrouver facilement.</li>
        </ol>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Twilio settings panel (SMS d'arrivée)
// ---------------------------------------------------------------------------
function TwilioPanel() {
  const [settings, setSettings] = useState(null);
  const [accountSid, setAccountSid] = useState("");
  const [authToken, setAuthToken] = useState("");
  const [fromNumber, setFromNumber] = useState("");
  const [saving, setSaving] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    api.get("/admin/twilio-settings").then((r) => {
      setSettings(r.data);
      setFromNumber(r.data.from_number || "");
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { from_number: fromNumber || null };
      if (accountSid.trim()) payload.account_sid = accountSid.trim();
      if (authToken.trim()) payload.auth_token = authToken.trim();
      const r = await api.patch("/admin/twilio-settings", payload);
      setSettings(r.data);
      setAccountSid(""); setAuthToken("");
      toast.success("Réglages Twilio sauvegardés");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testPhone.trim()) { toast.error("Entre un numéro de destination (+33...)"); return; }
    setTesting(true); setTestResult(null);
    try {
      const r = await api.post("/admin/twilio-settings/test", { to: testPhone.trim() });
      setTestResult({ ok: true, ...r.data });
      toast.success(`SMS envoyé — statut Twilio : ${r.data.status}`);
    } catch (err) {
      setTestResult({ ok: false, error: formatApiError(err) });
      toast.error(formatApiError(err));
    } finally {
      setTesting(false);
    }
  };

  if (!settings) return <div className="h-40 rounded-2xl bg-white/5 animate-pulse" />;
  const configured = !!settings.account_sid_preview;

  return (
    <div className="space-y-4">
      <div className={`rounded-2xl border p-4 flex items-center gap-3 ${configured ? "bg-emerald-500/10 border-emerald-500/30" : "bg-amber-500/10 border-amber-500/30"}`}>
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${configured ? "bg-emerald-500/20" : "bg-amber-500/20"}`}>
          <ChatCircleDots size={20} className={configured ? "text-emerald-400" : "text-amber-400"} weight="duotone" />
        </div>
        <div className="flex-1">
          <div className="font-display font-semibold">SMS Twilio {configured ? "connecté" : "non configuré"}</div>
          <div className="text-xs text-white/60">
            {configured ? `SID: ${settings.account_sid_preview} · Depuis: ${settings.from_number || "?"}` : "Sans Twilio, seul l'email est envoyé au client"}
          </div>
        </div>
      </div>

      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 space-y-3">
        <div>
          <Label className="text-white/60 text-[11px] uppercase tracking-widest">Account SID</Label>
          <Input value={accountSid} onChange={(e) => setAccountSid(e.target.value)} placeholder={configured ? "Laisser vide pour conserver" : "ACxxxxxxxx..."} className="bg-white/5 border-white/10 mt-1 font-mono text-sm" data-testid="twilio-sid" />
        </div>
        <div>
          <Label className="text-white/60 text-[11px] uppercase tracking-widest">Auth Token</Label>
          <Input type="password" value={authToken} onChange={(e) => setAuthToken(e.target.value)} placeholder={configured ? "Laisser vide pour conserver" : "Token secret Twilio"} className="bg-white/5 border-white/10 mt-1 font-mono text-sm" data-testid="twilio-token" />
        </div>
        <div>
          <Label className="text-white/60 text-[11px] uppercase tracking-widest">Numéro d&apos;envoi</Label>
          <Input value={fromNumber} onChange={(e) => setFromNumber(e.target.value)} placeholder="+33612345678" className="bg-white/5 border-white/10 mt-1 font-mono text-sm" data-testid="twilio-from" />
        </div>
        <Button onClick={save} disabled={saving} className="w-full h-12 bg-[#005CFF] hover:bg-[#0047CC] rounded-2xl" data-testid="twilio-save">
          <FloppyDisk size={16} /> {saving ? "…" : "Enregistrer"}
        </Button>
      </div>

      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 text-sm text-white/60">
        <div className="font-display font-semibold text-white mb-2">Récupérer vos clés Twilio</div>
        <ol className="space-y-1 list-decimal list-inside text-xs">
          <li>Créez un compte sur <span className="text-[#005CFF]">twilio.com</span> (essai gratuit avec crédit inclus).</li>
          <li>Depuis Console → Account → API keys : copiez <b>Account SID</b> et <b>Auth Token</b>.</li>
          <li>Achetez ou vérifiez un numéro dans Phone Numbers → Buy a Number (~1€/mois).</li>
          <li>Collez les 3 valeurs ci-dessus, enregistrez, et les techs pourront envoyer SMS d&apos;arrivée.</li>
        </ol>
      </div>

      {configured && (
        <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 space-y-3" data-testid="twilio-test-panel">
          <div className="flex items-start gap-2">
            <div className="w-9 h-9 rounded-xl bg-[#005CFF]/15 border border-[#005CFF]/30 flex items-center justify-center shrink-0">
              <PaperPlaneTilt size={16} className="text-[#7CB2FF]" weight="duotone" />
            </div>
            <div className="flex-1">
              <div className="font-display font-semibold text-sm">Envoyer un SMS de test</div>
              <div className="text-[11px] text-white/50">Valide la config sans passer par une intervention. Le SMS partira depuis <span className="font-mono">{settings.from_number}</span>.</div>
            </div>
          </div>
          <div className="flex gap-2">
            <Input
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              placeholder="+33612345678"
              className="bg-white/5 border-white/10 font-mono text-sm flex-1"
              data-testid="twilio-test-phone"
            />
            <Button
              onClick={sendTest}
              disabled={testing || !testPhone.trim()}
              className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40"
              data-testid="twilio-test-send"
            >
              <PaperPlaneTilt size={13} className="mr-1" /> {testing ? "…" : "Envoyer"}
            </Button>
          </div>
          {testResult && testResult.ok && (
            <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-2.5 text-[11px] text-emerald-200 space-y-0.5">
              <div className="flex items-center gap-1.5 font-semibold"><CheckCircle size={12} weight="fill" /> SMS envoyé avec succès</div>
              <div className="text-emerald-300/70 font-mono">SID : {testResult.sid} · statut : {testResult.status} · vers {testResult.to}</div>
              <div className="text-emerald-200/60">Vérifie que le destinataire l&apos;a bien reçu (peut prendre quelques secondes).</div>
            </div>
          )}
          {testResult && !testResult.ok && (
            <div className="rounded-lg bg-red-500/10 border border-red-500/30 p-2.5 text-[11px] text-red-200 space-y-0.5">
              <div className="flex items-center gap-1.5 font-semibold"><Warning size={12} weight="fill" /> Échec</div>
              <div className="text-red-300/80">{testResult.error}</div>
              <div className="text-red-200/60 text-[10px] mt-1">Rappel : en mode trial Twilio, le destinataire doit être un numéro vérifié dans Verified Caller IDs.</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Prod launch cleanup panel
// ---------------------------------------------------------------------------
function ProdCleanupPanel() {
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/admin/demo-data-stats");
      setStats(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  useEffect(() => { load(); }, []);

  const wipe = async () => {
    if (confirm !== "SUPPRIMER") return toast.error("Tapez SUPPRIMER pour confirmer");
    setBusy(true);
    try {
      const r = await api.post("/admin/cleanup-demo-data");
      const d = r.data.deleted;
      toast.success(`Nettoyé — ${d.clients} clients, ${d.chantiers} chantiers, ${d.interventions} interventions, ${d.demo_users || 0} utilisateurs démo supprimés`);
      setConfirm("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const total = stats ? Object.values(stats).reduce((a, b) => a + b, 0) : 0;

  return (
    <div className="space-y-4">
      <div className="rounded-2xl bg-gradient-to-br from-amber-500/15 to-orange-500/10 border border-amber-500/30 p-4 flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-amber-500/20 flex items-center justify-center">
          <Broom size={22} className="text-amber-400" weight="duotone" />
        </div>
        <div className="flex-1">
          <div className="font-display font-semibold">Préparation mise en production</div>
          <div className="text-xs text-white/70">Supprimez toutes les données de démonstration et de test avant de basculer en usage réel.</div>
        </div>
      </div>

      {stats && (
        <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 space-y-2">
          <div className="text-[11px] uppercase tracking-widest text-white/50 mb-2">Données démo détectées</div>
          <StatRow label="Clients de test" value={stats.clients} />
          <StatRow label="Chantiers de test" value={stats.chantiers} />
          <StatRow label="Interventions TEST_" value={stats.interventions} />
          <StatRow label="Notifications arrivée TEST_" value={stats.arrival_notifications_test} />
          <StatRow label="Utilisateurs démo (William, Tech Démo, ancien admin)" value={stats.demo_users || 0} />
          <div className="pt-2 border-t border-white/10 flex items-center justify-between">
            <div className="text-sm font-semibold">Total à supprimer</div>
            <div className="font-mono text-2xl text-amber-400">{total}</div>
          </div>
        </div>
      )}

      <div className="rounded-2xl bg-red-500/5 border border-red-500/30 p-4 space-y-3">
        <div className="flex items-start gap-2 text-xs text-red-300">
          <Warning size={16} weight="duotone" className="shrink-0 mt-0.5" />
          <div>
            <b>Action irréversible.</b> Tous les enregistrements dont le nom commence par <span className="font-mono">TEST_</span> ou correspondant aux entrées « Client Démo », « Rénovation démo », « NouveauClientSiri », « Chantier Siri Test » seront supprimés, ainsi que leurs interventions, tâches, documents, signatures et notifications. <b>Les utilisateurs démo (William, Tech Démo) et l&apos;ancien admin 0699999999 seront également supprimés.</b> Votre compte reste protégé.
          </div>
        </div>
        <div>
          <Label className="text-white/60 text-[11px] uppercase tracking-widest">Tapez « SUPPRIMER » pour confirmer</Label>
          <Input
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="SUPPRIMER"
            className="bg-white/5 border-red-500/30 mt-1 font-mono"
            data-testid="prod-confirm-input"
          />
        </div>
        <Button
          onClick={wipe}
          disabled={busy || total === 0 || confirm !== "SUPPRIMER"}
          className="w-full h-12 bg-red-500 hover:bg-red-600 rounded-2xl disabled:opacity-40"
          data-testid="prod-cleanup-btn"
        >
          <Broom size={16} weight="bold" /> {busy ? "Nettoyage…" : total === 0 ? "Aucune donnée démo" : `Supprimer les ${total} entrées démo`}
        </Button>
      </div>

      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 text-sm text-white/60">
        <div className="font-display font-semibold text-white mb-2">Checklist avant mise en production</div>
        <ol className="space-y-1.5 list-decimal list-inside text-xs">
          <li>Configurer <b>Axonaut</b> avec la vraie clé API dans l&apos;onglet Axonaut</li>
          <li>Configurer <b>Twilio</b> (SID + token + numéro d&apos;envoi) pour activer les SMS d&apos;arrivée</li>
          <li>Créer les <b>clients réels</b> avec email + téléphone valides</li>
          <li>Ajouter votre <b>équipe</b> (associés, techniciens) via le menu +</li>
          <li>Personnaliser les <b>fiches types</b> selon vos prestations récurrentes</li>
          <li>Supprimer les données démo via le bouton ci-dessus</li>
          <li>Changer le mot de passe admin par défaut si besoin</li>
        </ol>
      </div>
    </div>
  );
}

function StatRow({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-white/70">{label}</span>
      <span className={`font-mono ${value > 0 ? "text-amber-400" : "text-white/40"}`}>{value}</span>
    </div>
  );
}
